import "@/App.css";
import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./lib/theme-provider";
import { AuthProvider } from "./lib/auth-context";
import { UnitsProvider } from "./lib/units";
import { Toaster } from "./components/ui/sonner";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { InstallPrompt } from "./components/common/InstallPrompt";
import { registerServiceWorker } from "./lib/pwa";
import { initNativeShell, isNative, nativePlatform } from "./lib/native";
import { api } from "./lib/supabase";
import { toast } from "sonner";

import Landing from "./pages/Landing";
import Auth from "./pages/Auth";

import { MobileLayout } from "./components/mobile/MobileLayout";
import MobileHome from "./pages/mobile/Home";
import MobileEvents from "./pages/mobile/Events";
import MobileEventDetail from "./pages/mobile/EventDetail";
import MobileJoinEvent from "./pages/mobile/JoinEvent";
import MobileCheckIn from "./pages/mobile/CheckIn";
import MobileReady from "./pages/mobile/Ready";
import MobileLiveRace from "./pages/mobile/LiveRace";
import MobileFinishPending from "./pages/mobile/FinishPending";
import MobileResult from "./pages/mobile/Result";
import MobileCreate from "./pages/mobile/Create";
import MobileCreateEvent from "./pages/mobile/CreateEvent";
import StartPersonalRun from "./pages/mobile/StartPersonalRun";
import LivePersonalRun from "./pages/mobile/LivePersonalRun";
import RunSummary from "./pages/mobile/RunSummary";
import ScannerActivate from "./pages/mobile/ScannerActivate";
import Scanner from "./pages/mobile/Scanner";
import Leaderboard from "./pages/mobile/Leaderboard";
import Profile from "./pages/mobile/Profile";
import Friends from "./pages/mobile/Friends";
import Settings from "./pages/mobile/Settings";
import RunDetail from "./pages/mobile/RunDetail";
import EventLeaderboard from "./pages/mobile/EventLeaderboard";

function App() {
  useEffect(() => {
    // Web-only — service worker handles offline PWA + background sync. On
    // a Capacitor native shell we have real native plugins instead, no SW
    // needed (and in fact SW inside a webview causes caching headaches).
    if (!isNative()) {
      registerServiceWorker();
    }
    // Boot native shell: configure status bar, dismiss splash, and start
    // the push-notification pipeline. The push-token callback upserts into
    // Supabase device_tokens so the backend can target FCM/APNs. No-op on
    // web.
    initNativeShell({
      onPushToken: async (token) => {
        try {
          await api.savePushToken({
            token,
            provider: nativePlatform() === 'ios' ? 'apns' : 'fcm',
            userAgent: `rivl-native-${nativePlatform()}`,
          });
        } catch (err) {
          // Non-fatal — user can still use the app without push.
          console.warn('[rivl] push token upsert failed', err);
        }
      },
      onPushNotification: (n, source) => {
        // Payload shape (set by `_shared/push.ts`):
        //   { title, body, data: { url, kind, event_id } }
        const url = n?.data?.url;
        if (source === 'tap' && url) {
          // Full-page assign is the simplest cross-router nav from an
          // event handler mounted above the <BrowserRouter>. Users
          // tapping a notification expect a hard context switch anyway.
          window.location.assign(url);
          return;
        }
        if (source === 'foreground' && n?.title) {
          toast(n.title, { description: n.body });
        }
      },
    });
  }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <UnitsProvider>
          <div className="App">
            <BrowserRouter>
              <Routes>
                <Route path="/welcome" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/login" element={<Navigate to="/auth" replace />} />

                <Route element={<ProtectedRoute><MobileLayout /></ProtectedRoute>}>
                  <Route path="/" element={<MobileHome />} />
                  <Route path="/events" element={<MobileEvents />} />
                  <Route path="/events/:id" element={<MobileEventDetail />} />
                  <Route path="/events/:id/join" element={<MobileJoinEvent />} />
                  <Route path="/events/:id/checkin" element={<MobileCheckIn />} />
                  <Route path="/events/:id/ready" element={<MobileReady />} />
                  <Route path="/events/:id/race" element={<MobileLiveRace />} />
                  <Route path="/events/:id/finish" element={<MobileFinishPending />} />
                  <Route path="/events/:id/result" element={<MobileResult />} />
                  <Route path="/create" element={<MobileCreate />} />
                  <Route path="/events/new" element={<MobileCreateEvent />} />
                  <Route path="/run/start" element={<StartPersonalRun />} />
                  <Route path="/run/live" element={<LivePersonalRun />} />
                  <Route path="/run/summary" element={<RunSummary />} />
                  <Route path="/runs/:id" element={<RunDetail />} />
                  <Route path="/events/:id/leaderboard" element={<EventLeaderboard />} />
                  <Route path="/scanner/activate" element={<ScannerActivate />} />
                  <Route path="/scanner" element={<Scanner />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/friends" element={<Friends />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <InstallPrompt />
            <Toaster richColors position="top-center" />
          </div>
        </UnitsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
